import { useMemo } from "react";
import LeftMenuNavSection from "components/LeftMenuNavSection";
import FacetSection from "pages/projectsMainPage/leftMenu/FacetSection";
import SeverityFilter from "./SeverityFilter.jsx";

function buildFacets(projects, field, searchTerm) {
  const counts = new Map();
  for (const p of projects) {
    const value = p[field];
    if (!value) continue;
    counts.set(value, (counts.get(value) || 0) + 1);
  }
  const term = searchTerm?.toLowerCase() ?? "";
  return Array.from(counts.entries())
    .map(([value, count]) => ({ value, count }))
    .filter((f) => !term || f.value.toLowerCase().includes(term))
    .sort((a, b) => a.value.localeCompare(b.value, undefined, { sensitivity: "base" }));
}

export default function StockIssuesLeftMenu({
  projects,
  severity,
  onSeverityChange,
  selectedDistributor,
  selectedRep,
  onDistributorChange,
  onRepChange,
  facetSearch,
  onFacetSearchChange,
  onMenuClose,
  onOpenDismissed,
}) {
  // Cross-filter facets: the distributor list narrows by the selected rep, and
  // the rep list narrows by the selected distributor. A facet never filters
  // itself — otherwise the user couldn't see siblings to switch to.
  const distributorFacets = useMemo(() => {
    const scoped = selectedRep
      ? projects.filter((p) => p.rep === selectedRep)
      : projects;
    return buildFacets(
      scoped,
      "distributor",
      facetSearch?.field === "distributor" ? facetSearch.searchTerm : "",
    );
  }, [projects, selectedRep, facetSearch]);

  const repFacets = useMemo(() => {
    const scoped = selectedDistributor
      ? projects.filter((p) => p.distributor === selectedDistributor)
      : projects;
    return buildFacets(
      scoped,
      "rep",
      facetSearch?.field === "rep" ? facetSearch.searchTerm : "",
    );
  }, [projects, selectedDistributor, facetSearch]);

  return (
    <div className="grid h-full grid-rows-[auto_auto_1fr_auto] gap-4 overflow-hidden">
      <LeftMenuNavSection
        location={{ name: "Main", url: "/private/search", direction: "left" }}
      />
      <div className="px-2">
        <SeverityFilter value={severity} onChange={onSeverityChange} />
      </div>
      <div className="h-full overflow-auto">
        <div className="grid h-full min-h-[500px] grid-rows-[0.5fr_0.5fr]">
          <div className="h-full overflow-y-hidden px-2">
            <FacetSection
              title="Distributors"
              facetField="distributor"
              data={distributorFacets}
              currentFilter={selectedDistributor}
              onSelectFacet={(value) => {
                if (!selectedDistributor) onMenuClose?.();
                onDistributorChange(
                  value === selectedDistributor ? null : value,
                );
              }}
              currentFacetSearch={facetSearch}
              onFacetSearchTermChanged={onFacetSearchChange}
            />
          </div>
          <div className="h-full overflow-y-hidden px-2">
            <FacetSection
              title="Reps"
              facetField="rep"
              data={repFacets}
              currentFilter={selectedRep}
              onSelectFacet={(value) => {
                if (!selectedRep) onMenuClose?.();
                onRepChange(value === selectedRep ? null : value);
              }}
              currentFacetSearch={facetSearch}
              onFacetSearchTermChanged={onFacetSearchChange}
            />
          </div>
        </div>
      </div>
      <div className="border-t border-white/15 px-3 py-3">
        <button
          type="button"
          onClick={onOpenDismissed}
          className="group flex w-full items-center justify-between text-xs font-semibold uppercase tracking-wider text-white/70 hover:text-st-orange"
        >
          <span>Dismissed</span>
          <span className="transition-transform group-hover:translate-x-0.5">→</span>
        </button>
      </div>
    </div>
  );
}
